/**
 * Session 50 §5e.bis — PWA offline queue for answers solved in a dead-zone.
 *
 * In offline mode the PWA verifies site codes locally (bcrypt) and updates
 * its own state without hitting the backend. On reconnect we POST all queued
 * entries to `/api/games/{code}/sync` so the server can re-verify, advance
 * the team, and broadcast a TEAM_CAUGHT_UP event to teammates.
 *
 * Storage choice: localStorage, NOT IndexedDB. Rationale:
 *   - Urban-quest dead-zones are minutes, not hours — queue size stays tiny
 *     (maybe 1-5 entries per outage).
 *   - localStorage is synchronous + survives reload + simple contract.
 *   - The whole queue is scoped per gameCode so a player resuming a
 *     different game's queue from a previous session is impossible.
 *
 * Threat model note: the queue is client-side only. A malicious player
 * could tamper with the localStorage entry and post fake payloads — that's
 * the SAME threat the /sync endpoint already defends against via bcrypt
 * re-verification and the server-derived teamId (§5e.bis rules 4 + 2).
 */

export interface OfflineQueueEntry {
  locationId: string;
  submittedAnswer: string;
  wasSkip: boolean;
  /** Captured at enqueue time for debugging / UX only.
   *  The server IGNORES this value (§5e.bis rule 6) and stamps its own
   *  submittedAt on every Answers row. */
  completedAt: number;
}

const STORAGE_PREFIX = 'aq_sync_queue_';

function keyFor(gameCode: string): string {
  return STORAGE_PREFIX + gameCode;
}

/** Read all queued entries for this game. Never throws — a corrupt entry
 *  is treated as an empty queue so the caller can always proceed. */
export function readQueue(gameCode: string): OfflineQueueEntry[] {
  if (!gameCode) return [];
  try {
    const raw = localStorage.getItem(keyFor(gameCode));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e: unknown): e is OfflineQueueEntry =>
      typeof e === 'object' && e !== null &&
      typeof (e as OfflineQueueEntry).locationId === 'string' &&
      typeof (e as OfflineQueueEntry).submittedAnswer === 'string' &&
      typeof (e as OfflineQueueEntry).wasSkip === 'boolean' &&
      typeof (e as OfflineQueueEntry).completedAt === 'number'
    );
  } catch {
    return [];
  }
}

/** Append an entry, preserving order. Duplicate entries for the SAME
 *  locationId are allowed at enqueue time — the server /sync endpoint
 *  dedups via the route-position pre-check and SkipPenalties unique
 *  index, so one more client-side dedup would be belt-and-suspenders. */
export function enqueue(gameCode: string, entry: OfflineQueueEntry): void {
  if (!gameCode) return;
  const current = readQueue(gameCode);
  current.push(entry);
  try {
    localStorage.setItem(keyFor(gameCode), JSON.stringify(current));
  } catch {
    // Storage full / disabled — drop silently. An organizer running a game
    // in Safari private mode would see the whole offline feature degrade,
    // but that's better than the submit flow throwing.
  }
}

/** Wipe the queue for this game. Called on successful flush so a replay
 *  of the same browser window doesn't double-submit. */
export function clearQueue(gameCode: string): void {
  if (!gameCode) return;
  try {
    localStorage.removeItem(keyFor(gameCode));
  } catch {
    /* noop */
  }
}

/** Count without reading every entry's payload. Used by the UI chip. */
export function queueSize(gameCode: string): number {
  return readQueue(gameCode).length;
}
